import { Luggage, Backpack, AlertTriangle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

interface BaggageInfoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  baggageIncluded: boolean
}

const BAGGAGE_INFO = {
  cabin: {
    weight: "8 kg",
    dimensions: "40 x 30 x 20 cm",
  },
  checked: {
    weight: "23 kg",
    dimensions: "158 cm total (length + width + height)",
    maxSingleDim: "No single dimension may exceed 120 cm",
    note: "Each passenger may check up to 1 bag. Additional bags subject to surcharge.",
  },
  prohibited: [
    "Flammable liquids and gases",
    "Sharp objects in cabin baggage",
    "Lithium batteries in checked baggage",
    "Explosives and fireworks",
  ],
}

export function BaggageInfoModal({
  open,
  onOpenChange,
  baggageIncluded,
}: BaggageInfoModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl border-border bg-card p-0"
      >
        <div className="p-6 pb-0">
          <DialogHeader>
            <div
              className="mb-3 flex size-11 items-center justify-center rounded-xl"
              style={{ background: "color-mix(in oklch, var(--primary) 12%, transparent)" }}
            >
              <Luggage className="size-5 text-primary" />
            </div>
            <DialogTitle className="text-lg font-bold tracking-wide text-foreground uppercase">
              Baggage Allowance
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {baggageIncluded
                ? "Your fare includes checked baggage. See details below."
                : "This fare does not include checked baggage. Cabin baggage rules still apply."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-4 p-6">
          <BaggageCard
            icon={<Backpack className="size-4 text-primary" />}
            title="Cabin Baggage"
            included
          >
            <InfoRow label="Weight" value={BAGGAGE_INFO.cabin.weight} />
            <InfoRow label="Dimensions" value={BAGGAGE_INFO.cabin.dimensions} />
          </BaggageCard>

          <BaggageCard
            icon={<Luggage className="size-4 text-primary" />}
            title="Checked Baggage"
            included={baggageIncluded}
          >
            <InfoRow label="Weight" value={BAGGAGE_INFO.checked.weight} />
            <InfoRow
              label="Max dimensions"
              value={BAGGAGE_INFO.checked.dimensions}
            />
            <InfoRow
              label="Single dimension"
              value={BAGGAGE_INFO.checked.maxSingleDim}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              {BAGGAGE_INFO.checked.note}
            </p>
          </BaggageCard>

          <div className="rounded-xl border border-border p-4">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="size-4 text-[#ffb547]" />
              <span className="text-xs font-semibold tracking-wide text-foreground uppercase">
                Prohibited Items
              </span>
            </div>
            <ul className="ml-6 list-disc space-y-1">
              {BAGGAGE_INFO.prohibited.map((item) => (
                <li key={item} className="text-xs text-muted-foreground">
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-center text-[11px] text-muted-foreground">
            Information is indicative — final rules depend on the operating
            carrier.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function BaggageCard({
  icon,
  title,
  included,
  children,
}: {
  icon: React.ReactNode
  title: string
  included: boolean
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="mb-3 flex items-center gap-2.5">
        {icon}
        <span className="text-sm font-bold text-foreground">{title}</span>
        <span
          className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${
            included
              ? "bg-primary/15 text-primary"
              : "bg-white/5 text-muted-foreground"
          }`}
        >
          {included ? "Included" : "Not included"}
        </span>
      </div>
      {children}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-foreground">{value}</span>
    </div>
  )
}
